/**
 * Utilities for encoding and decoding filter state to/from URL parameters
 * This enables shareable and bookmarkable filtered product views
 */

export interface FilterState {
  [key: string]: string[];
}

/**
 * Converts filter state to URL search parameters
 * Uses 'f_' prefix to avoid conflicts with existing params (page, sort, q)
 * Complex JSON values are base64 encoded for URL safety
 */
export const encodeFiltersToURL = (filters: FilterState): URLSearchParams => {
  const searchParams = new URLSearchParams();

  Object.entries(filters).forEach(([filterName, values]) => {
    if (values.length === 0) return;

    // Create URL-safe parameter name
    const paramName = `f_${filterName.toLowerCase().replace(/\s+/g, "_")}`;

    // Handle different types of filter values
    const encodedValues = values.map((value) => {
      try {
        // Check if value is JSON (like colors or categories)
        JSON.parse(value);
        // If it's valid JSON, base64 encode it for URL safety
        return btoa(value);
      } catch {
        // Plain string value, just URL encode it
        return encodeURIComponent(value);
      }
    });

    // Join multiple values with comma
    searchParams.set(paramName, encodedValues.join(","));
  });

  return searchParams;
};

/**
 * Extracts filter state from URL search parameters
 * Decodes base64 encoded JSON values and URL encoded strings
 */
export const decodeFiltersFromURL = (
  searchParams: URLSearchParams
): FilterState => {
  const filters: FilterState = {};

  // Process all parameters that start with 'f_'
  Array.from(searchParams.entries()).forEach(([paramName, paramValue]) => {
    if (!paramName.startsWith("f_")) return;

    // Convert parameter name back to filter name
    const filterName = paramName
      .substring(2) // Remove 'f_' prefix
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\b\w/g, (l) => l.toUpperCase()); // Capitalize first letter of each word

    // Split comma-separated values
    const encodedValues = paramValue.split(",");

    // Decode each value
    const decodedValues = encodedValues
      .map((encodedValue) => {
        try {
          // Try to decode as base64 (for JSON values)
          const decoded = atob(encodedValue);
          // Verify it's valid JSON
          JSON.parse(decoded);
          return decoded;
        } catch {
          try {
            // If not base64, try URL decoding
            return decodeURIComponent(encodedValue);
          } catch {
            // If all else fails, return as-is
            return encodedValue;
          }
        }
      })
      .filter((value) => value.length > 0); // Remove empty values

    if (decodedValues.length > 0) {
      filters[filterName] = decodedValues;
    }
  });

  return filters;
};

/**
 * Updates URL with current filter state while preserving other parameters
 */
export const updateURLWithFilters = (
  filters: FilterState,
  searchParams: URLSearchParams,
  setSearchParams: (params: URLSearchParams) => void
) => {
  // Create new search params starting with existing non-filter params
  const newSearchParams = new URLSearchParams();

  // Preserve non-filter parameters (page, sort, q, etc.)
  Array.from(searchParams.entries()).forEach(([key, value]) => {
    if (!key.startsWith("f_")) {
      newSearchParams.set(key, value);
    }
  });

  // Add filter parameters
  const filterParams = encodeFiltersToURL(filters);
  Array.from(filterParams.entries()).forEach(([key, value]) => {
    newSearchParams.set(key, value);
  });

  // Reset page to 1 when filters change
  newSearchParams.set("page", "1");

  setSearchParams(newSearchParams);
};

/**
 * Removes all filter parameters from URL
 */
export const clearFiltersFromURL = (
  searchParams: URLSearchParams,
  setSearchParams: (params: URLSearchParams) => void
) => {
  const newSearchParams = new URLSearchParams();

  // Keep only non-filter parameters
  Array.from(searchParams.entries()).forEach(([key, value]) => {
    if (!key.startsWith("f_")) {
      newSearchParams.set(key, value);
    }
  });

  // Reset page to 1
  newSearchParams.set("page", "1");

  setSearchParams(newSearchParams);
};

/**
 * Checks if URL contains any filter parameters
 */
export const hasFiltersInURL = (searchParams: URLSearchParams): boolean => {
  return Array.from(searchParams.keys()).some((key) => key.startsWith("f_"));
};
